/**
 * De QUEM é a entrega que o turno acabou de produzir — spec-001
 * `project-home-ia-skills/preparacao-reuniao`, G-R2-9 do QA it.2.
 *
 * ## O defeito que isto fecha
 *
 * Com o chip fixado no Murilo (22278) e `/pré-reunião Ilo Santos` digitado, a
 * pauta sai certa (a entrega é do Ilo, 6515) e a CONVERSA continua ancorada no
 * Murilo: `conversas.id_cliente_app = 22278`, cabeçalho nomeando o Murilo, e o
 * `run_objetos` do mesmo turno com `preparo-reuniao/6515`. Duas verdades
 * caladas na mesma conversa — e o dano é no turno SEGUINTE: contexto, memória
 * e histórico passam a ser do cliente errado, sem nada avisar.
 *
 * ## Por que ler o CARTÃO, e não o evento
 *
 * O canal certo para "o servidor resolveu um cliente" é o evento SSE
 * `cliente-identificado` (`apiChat::_onClienteIdentificado`, que já reancora e
 * grava o vínculo). **Medido no fio em 19/09/2026**, o turno do atalho NÃO o
 * emite — os eventos são `start · tool_call · tool-input-available · plano ·
 * tool_result · tool-output-available · token · text-delta · end · finish`. O
 * servidor resolveu o cliente (o `ui.props` traz `id_cliente_app: 6515`) e não
 * o declarou pelo canal de contexto.
 *
 * O conserto de RAIZ é a tool declarar o cliente (backend, uma linha). Até lá,
 * o cartão é a declaração que existe: ele diz "esta preparação é do cliente X",
 * e é o próprio servidor quem a escreve. ⚠️ Quem reancora com isto **grava no
 * servidor pela mesma porta da troca explícita** (`vincularCliente`) — o front
 * que trocasse só o chip recriaria o defeito do outro lado.
 *
 * ## A lista é DECLARADA, não derivada
 *
 * Só a chave de cartão que, por contrato, descreve UM cliente entra aqui.
 * `documento/panorama` (escritório inteiro) e cartões de leitura de módulo
 * ficam de fora: "tem `id_cliente_app` no payload" é verdade em dezenas de
 * cartões e não significa que a ENTREGA é daquele cliente.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */

/**
 * As chaves cujo `ui.props.id_cliente_app` É o cliente da entrega.
 *
 * Cartão novo de entrega por cliente entra aqui no mesmo PR que o registra —
 * ficar de fora significa só não reancorar, nunca reancorar errado.
 */
export const CHAVES_DE_ENTREGA_POR_CLIENTE = Object.freeze([
  'documento/preparo-de-reuniao',
])

const numeroOuNulo = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null
  const numero = Number(valor)
  return Number.isFinite(numero) ? numero : null
}

/** A parte de entrega por cliente dentro do `tool_calls[]` de uma mensagem. */
function idNasPartes(toolCalls) {
  const partes = Array.isArray(toolCalls) ? toolCalls : []
  for (const parte of partes) {
    const ui = (parte && parte.ui) || null
    if (!ui || CHAVES_DE_ENTREGA_POR_CLIENTE.indexOf(ui.component) === -1) continue
    const id = numeroOuNulo((ui.props || {}).id_cliente_app)
    if (id !== null) return id
  }
  return null
}

/**
 * O `id_cliente_app` da entrega da ÚLTIMA mensagem do assistente — ou `null`.
 *
 * A ÚLTIMA, e só ela: o turno que acabou é o que pode reancorar. Uma entrega
 * de três turnos atrás não muda o foco de agora — seria a conversa andando
 * para trás sozinha.
 */
export function clienteDaEntregaDoTurno(mensagens) {
  const lista = Array.isArray(mensagens) ? mensagens : []
  for (let i = lista.length - 1; i >= 0; i -= 1) {
    const mensagem = lista[i]
    if (!mensagem || mensagem.role !== 'assistant') continue
    return idNasPartes(mensagem.tool_calls)
  }
  return null
}

/**
 * Precisa reancorar? `true` só quando a entrega declara um cliente E ele é
 * DIFERENTE do que está em foco.
 *
 * ⚠️ Sem cliente em foco (conversa geral) TAMBÉM reancora: o consultor
 * nomeou alguém, e a entrega é dessa pessoa — deixar a conversa sem âncora
 * seria perder a escolha que ele acabou de fazer.
 */
export function precisaReancorar(idDaEntrega, idEmFoco) {
  const alvo = numeroOuNulo(idDaEntrega)
  if (alvo === null) return false
  return alvo !== numeroOuNulo(idEmFoco)
}
